import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { AtomicLoadingScreen } from '../ui/atomic-loader';
import { toast } from '../ui/use-toast';
import { apiService } from '@/lib/api';
import jsPDF from 'jspdf';
import EmailTemplatesManager from '@/components/email/EmailTemplatesManager';
import { 
  MessageSquare, 
  Plus, 
  Edit2, 
  Trash2, 
  Shield, 
  Zap, 
  Eye, 
  Save,
  X,
  Copy,
  Settings,
  Download,
  ChevronDown
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';

interface WhatsAppConnection {
  id: number;
  name: string;
  connection_type: 'oficial' | 'extra_oficial';
  is_active: boolean;
  client_id: number;
}

interface ConnectionsResponse {
  connections: WhatsAppConnection[];
}

interface TemplatesResponse {
  templates: MessageTemplate[];
}

interface MetaTemplatesResponse {
  templates: MetaTemplate[];
}

interface MetaTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  status: string;
  components: {
    type: string;
    text: string;
  }[];
}

interface MessageTemplate {
  id: number;
  whatsapp_connection_id: number;
  template_type: string;
  name: string;
  content: string;
  variables: { [key: string]: string };
  is_active: boolean;
  strategy_type?: 'LEMBRETE' | 'COBRANÇA' | 'REVISTORIA' | 'REATIVACAO' | 'PADRAO';
  is_special_template?: boolean;
  tag_code?: string;
  created_at: string;
  updated_at: string;
}

/** Config V2 (ÁtomosChat) — espelha o DTO do backend (`/atomos-chat/client/:id/configs`). */
interface AtomosChatConfigDto {
  id: number;
  account_name: string;
  channel_name?: string | null;
  channel_phone?: string | null;
  channel_id?: string | null;
  is_active?: boolean;
}

/** Template V2 (ÁtomosChat) — apenas leitura aqui; criação é feita em chat.atomos.tech. */
interface AtomosChatTemplate {
  id: string;
  name: string;
  text?: string;
  status: string;
  categoryName?: string;
  fileType?: string;
}

interface MessagesTabProps {
  clientId: number;
}

const templateTypes = [
  { value: 'boas_vindas', label: 'Boas-vindas' },
  { value: 'lembrete', label: 'Lembrete' },
  { value: 'lembrete_dia', label: 'Lembrete Dia' },
  { value: 'cobranca', label: 'Cobrança' },
  { value: 'revistoria', label: 'Revistoria' },
  { value: 'reativacao', label: 'Reativação' },
  { value: 'recuperacao', label: 'Recuperação' }
];

// Função para gerar saudação dinâmica baseada no horário
const getSaudacao = (): string => {
  const hora = new Date().getHours();
  if (hora >= 5 && hora <= 12) {
    return 'Bom dia';
  } else if (hora >= 13 && hora <= 18) {
    return 'Boa tarde';
  } else {
    return 'Boa noite';
  }
};

// Opções de mapeamento para as variáveis
const variableMappingOptions = [
  { value: 'saudacao', label: 'Saudação' },
  { value: 'nome_associado', label: 'Nome do associado' },
  { value: 'endereco', label: 'Endereço' },
  { value: 'placa', label: 'Placa do veículo' },
  { value: 'modelo_veiculo', label: 'Modelo do veículo' },
  { value: 'data_vencimento', label: 'Data de vencimento' },
  { value: 'vencimento_boas_vindas', label: 'Vencimento (Boas-vindas)' },
  { value: 'valor_boleto', label: 'Valor do boleto' },
  { value: 'linha_digitavel', label: 'Linha Digitável (Hinova)' },
  { value: 'beneficios', label: 'Benefícios (Hinova)' },
];

export function MessagesTab({ clientId }: MessagesTabProps) {
  const [connections, setConnections] = useState<WhatsAppConnection[]>([]);
  const [tipoMensagem, setTipoMensagem] = useState<'whatsapp' | 'email'>('whatsapp');
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [metaTemplates, setMetaTemplates] = useState<MetaTemplate[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<WhatsAppConnection | null>(null);
  // V2 (ÁtomosChat): múltiplas configs por cliente. Quando uma é selecionada,
  // os templates Meta/V1 são esvaziados e carregamos os templates V2.
  const [atomosConfigs, setAtomosConfigs] = useState<AtomosChatConfigDto[]>([]);
  const [selectedAtomosConfigId, setSelectedAtomosConfigId] = useState<number | null>(null);
  const [atomosTemplates, setAtomosTemplates] = useState<AtomosChatTemplate[]>([]);
  const [loadingAtomosTemplates, setLoadingAtomosTemplates] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplate | null>(null);
  const [showPreview, setShowPreview] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMetaTemplate, setEditingMetaTemplate] = useState<MetaTemplate | null>(null);
  const [editFormData, setEditFormData] = useState({
    content: '',
    variables: {} as { [key: string]: string }
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState<MetaTemplate | null>(null);
  const [viewFormData, setViewFormData] = useState({
    content: '',
    variables: {} as { [key: string]: string }
  });
  
  // Form states
  const [formData, setFormData] = useState({
    template_type: '',
    name: '',
    content: '',
    variables: {} as { [key: string]: string }
  });

  // Estados para mapeamento de variáveis
  const [variableMappings, setVariableMappings] = useState({
    '{{1}}': '',
    '{{2}}': '',
    '{{3}}': '',
    '{{4}}': '',
    '{{5}}': '',
    '{{6}}': ''
  } as { [key: string]: string });

  const [showVariableMappingModal, setShowVariableMappingModal] = useState(false);
  const [editingTemplateForMapping, setEditingTemplateForMapping] = useState<MessageTemplate | null>(null);

  useEffect(() => {
    loadConnections();
    loadAtomosConfigs();
  }, [clientId]);

  // Carrega templates V2 quando uma config V2 é selecionada
  useEffect(() => {
    if (selectedAtomosConfigId != null) {
      loadAtomosTemplates(selectedAtomosConfigId);
    } else {
      setAtomosTemplates([]);
    }
  }, [selectedAtomosConfigId, clientId]);

  useEffect(() => {
    if (selectedConnection) {
      console.log('🔍 Conexão selecionada:', selectedConnection);
      console.log('🔍 Tipo de conexão:', selectedConnection.connection_type);
      
      if (selectedConnection.connection_type === 'oficial') {
        console.log('🔍 Carregando templates da Meta para conexão oficial');
        loadMetaTemplates(selectedConnection.id);
      } else {
        console.log('🔍 Carregando templates customizados para conexão extra oficial');
        loadTemplates(selectedConnection.id);
      }
    }
  }, [selectedConnection]);

  const loadConnections = async () => {
    try {
      console.log('🔍 [MessagesTab] Carregando conexões para clientId:', clientId);
      const data = await apiService.get(`/whatsapp/client/${clientId}`) as ConnectionsResponse;
      console.log('🔍 [MessagesTab] Todas as conexões recebidas:', data.connections);
      
      // Log detalhado da estrutura da resposta
      console.log('🔍 [MessagesTab] DEBUG integração (shape):', {
        hasResponse: !!data,
        hasConnections: !!data?.connections,
        connectionsCount: data?.connections?.length || 0,
        keys: data ? Object.keys(data) : []
      });
      
      // Filtrar conexões ativas (tanto oficiais quanto extra oficiais)
      const activeConnections = data.connections.filter((conn: WhatsAppConnection) => 
        conn.is_active
      );
      console.log('🔍 [MessagesTab] Conexões ativas filtradas:', activeConnections);
      
      setConnections(activeConnections);
      
      if (activeConnections.length > 0) {
        // Priorizar conexões oficiais primeiro, depois extra oficiais
        const officialConnections = activeConnections.filter(conn => conn.connection_type === 'oficial');
        const extraOfficialConnections = activeConnections.filter(conn => conn.connection_type === 'extra_oficial');
        
        const prioritizedConnections = [...officialConnections, ...extraOfficialConnections];
        console.log('🔍 [MessagesTab] Usando primeira conexão priorizada:', prioritizedConnections[0]);
        setSelectedConnection(prioritizedConnections[0]);
      } else {
        console.log('⚠️ [MessagesTab] Nenhuma conexão ativa encontrada');
      }
    } catch (err) {
      console.error('❌ [MessagesTab] Erro ao verificar integração existente:', {
        message: (err as any)?.message,
        stack: (err as any)?.stack,
        status: (err as any)?.response?.status,
        data: (err as any)?.response?.data
      });
      toast({
        title: "Erro",
        description: "Erro ao carregar conexões WhatsApp",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async (connectionId: number) => {
    try {
      setIsLoading(true);
      const data = await apiService.get(`/message-templates/connection/${connectionId}`) as TemplatesResponse;
      setTemplates(data.templates);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar templates",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAtomosConfigs = async () => {
    try {
      const data = await apiService.get(`/atomos-chat/client/${clientId}/configs`) as { success?: boolean; configs?: AtomosChatConfigDto[] };
      const configs = (data?.configs ?? []).filter((c) => c.is_active !== false);
      setAtomosConfigs(configs);
    } catch (e) {
      console.error('[MessagesTab] loadAtomosConfigs:', e);
      setAtomosConfigs([]);
    }
  };

  const loadAtomosTemplates = async (configId: number) => {
    try {
      setLoadingAtomosTemplates(true);
      const data = await apiService.get(`/atomos-chat/client/${clientId}/templates?config_id=${configId}`) as { success?: boolean; templates?: AtomosChatTemplate[] };
      setAtomosTemplates(Array.isArray(data?.templates) ? data!.templates! : []);
    } catch (e) {
      console.error('[MessagesTab] loadAtomosTemplates:', e);
      setAtomosTemplates([]);
      toast({ title: 'Erro', description: 'Falha ao carregar templates da conexão V2', variant: 'destructive' });
    } finally {
      setLoadingAtomosTemplates(false);
    }
  };

  const loadMetaTemplates = async (connectionId: number) => {
    try {
      console.log('🔍 Carregando templates da Meta para connectionId:', connectionId);
      setIsLoading(true);
      const data = await apiService.get(`/whatsapp/${connectionId}/templates`) as MetaTemplatesResponse;
      console.log('🔍 Templates da Meta carregados:', data);
      setMetaTemplates((data as MetaTemplatesResponse).templates || []);
    } catch (error) {
      console.error('❌ Erro ao carregar templates da Meta:', error);
      setMetaTemplates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const extractVariables = (content: string, connectionType: string): string[] => {
    if (connectionType === 'oficial') {
      // API Oficial Meta: extrair {{1}}, {{2}}, etc.
      const matches = content.match(/\{\{\d+\}\}/g) || [];
      return [...new Set(matches)].sort();
    } else {
      // API Extra Oficial: extrair {nome}, {placa}, etc.
      const matches = content.match(/\{[^{}]+\}/g) || [];
      return [...new Set(matches)].sort();
    }
  };

  const handleContentChange = (content: string) => {
    setFormData(prev => ({ ...prev, content }));
    
    if (selectedConnection) {
      const variables = extractVariables(content, selectedConnection.connection_type);
      const newVariables: { [key: string]: string } = {};
      
      // Manter valores existentes e adicionar novos
      variables.forEach(variable => {
        newVariables[variable] = formData.variables[variable] || '';
      });
      
      setFormData(prev => ({ ...prev, variables: newVariables }));
    }
  };

  const handleEditContentChange = (content: string) => {
    setEditFormData(prev => ({ ...prev, content }));
    
    if (selectedConnection) {
      const variables = extractVariables(content, selectedConnection.connection_type);
      const newVariables: { [key: string]: string } = {};
      
      // Manter valores existentes e adicionar novos
      variables.forEach(variable => {
        newVariables[variable] = editFormData.variables[variable] || '';
      });
      
      setEditFormData(prev => ({ ...prev, variables: newVariables }));
    }
  };

  const generatePreview = (content: string, variables: { [key: string]: string }) => {
    let preview = content;
    
    Object.entries(variables).forEach(([key, value]) => {
      if (value) {
        const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        preview = preview.replace(regex, value);
      }
    });
    
    return preview;
  };

  // Função para gerar preview com mapeamento de variáveis
  const generateMappedPreview = (content: string, mappings: { [key: string]: string }) => {
    let preview = content;
    
    console.log('🔍 [generateMappedPreview] Iniciando preview com mapeamento:', {
      content: content.substring(0, 100) + '...',
      mappings,
      mappingsKeys: Object.keys(mappings)
    });
    
    // Dados de exemplo para preview (em produção viriam do banco public.disparos_boletos)
    const sampleData = {
      saudacao: getSaudacao(),
      nome_associado: 'João Silva',
      placa: 'ABC1234',
      modelo_veiculo: 'Civic XLS',
      data_vencimento: '20/07/2025',
      valor_boleto: 'R$ 100,00',
      beneficios: '• Chave reserva\n• Veículo extra',
    };

    console.log('🔍 [generateMappedPreview] Dados de exemplo:', sampleData);

    // Substituir cada placeholder pelo valor mapeado
    Object.entries(mappings).forEach(([placeholder, field]) => {
      if (field && sampleData[field as keyof typeof sampleData]) {
        const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        const value = sampleData[field as keyof typeof sampleData];
        preview = preview.replace(regex, value);
        console.log(`🔍 [generateMappedPreview] Substituindo ${placeholder} por ${value}`);
      } else {
        console.log(`⚠️ [generateMappedPreview] Campo não encontrado ou vazio: ${placeholder} -> ${field}`);
      }
    });
    
    console.log('🔍 [generateMappedPreview] Preview final:', preview.substring(0, 200) + '...');
    return preview;
  };

  // Função para abrir modal de mapeamento de variáveis
  const openVariableMappingModal = (template: MessageTemplate) => {
    setEditingTemplateForMapping(template);
    setShowVariableMappingModal(true);
  };

  // Função para salvar mapeamento de variáveis
  const saveVariableMapping = async () => {
    if (!editingTemplateForMapping) return;

    try {
      // Preparar dados para salvar no banco
      const mappingData = {
        template_id: editingTemplateForMapping.id,
        variable_mappings: variableMappings,
        // Mapeamento dos campos do banco public.disparos_boletos
        field_mappings: {
          saudacao: 'dynamic', // Calculado dinamicamente
          nome_associado: 'nome_associado',
          placa: 'placa',
          modelo_veiculo: 'modelo_veiculo',
          data_vencimento: 'data_vencimento',
          valor_boleto: 'valor_boleto',
          beneficios: 'beneficios'
        }
      };

      await apiService.put(`/message-templates/${editingTemplateForMapping.id}/mappings`, mappingData);

      toast({
        title: "Sucesso",
        description: "Mapeamento de variáveis salvo com sucesso"
      });

      setShowVariableMappingModal(false);
      setEditingTemplateForMapping(null);
      loadTemplates(selectedConnection!.id);
    } catch (error) {
      console.error('Erro ao salvar mapeamento:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar mapeamento de variáveis",
        variant: "destructive"
      });
    }
  };

  const openEditModal = (template: MetaTemplate) => {
    setEditingMetaTemplate(template);
    
    // Extrair conteúdo do template da Meta
    let content = '';
    if (template.components && template.components.length > 0) {
      const textComponent = template.components.find(comp => comp.type === 'BODY');
      if (textComponent && textComponent.text) {
        content = textComponent.text;
      }
    }
    
    const variables = extractVariables(content, 'oficial');
    const newVariables: { [key: string]: string } = {};
    variables.forEach(variable => {
      newVariables[variable] = '';
    });
    
    setEditFormData({
      content,
      variables: newVariables
    });
    
    setShowEditModal(true);
  };

  const openViewModal = (template: MetaTemplate) => {
    setViewingTemplate(template);
    
    // Extrair conteúdo do template da Meta
    let content = '';
    if (template.components && template.components.length > 0) {
      const textComponent = template.components.find(comp => comp.type === 'BODY');
      if (textComponent && textComponent.text) {
        content = textComponent.text;
      }
    }
    
    // Gerar preview com dados de exemplo - substituir cada placeholder individualmente
    const sampleData = [
      getSaudacao(), // {{1}}
      'João Silva',  // {{2}}
      'ABC1234',     // {{3}}
      'Civic XLS',   // {{4}}
      '20/07/2025',  // {{5}}
      'R$ 100,00'    // {{6}}
    ];

    let preview = content;
    for (let i = 1; i <= 6; i++) {
      const placeholder = `{{${i}}}`;
      const value = sampleData[i - 1];
      preview = preview.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
    }
    
    setViewFormData({
      content: preview,
      variables: {}
    });
    
    setShowViewModal(true);
  };

  const getTemplateContent = (template: MetaTemplate): string => {
    if (template.components && template.components.length > 0) {
      const textComponent = template.components.find(comp => comp.type === 'BODY');
      if (textComponent && textComponent.text) {
        return textComponent.text;
      }
    }
    return 'Conteúdo não disponível';
  };

  const saveTemplateChanges = async (templateId: string, updatedContent: string) => {
    if (!selectedConnection) return;
    
    try {
      // Preparar componentes para a API Meta
      const components = [
        {
          type: 'BODY',
          text: updatedContent
        }
      ];

      // Criar novo template na API Meta
      const result = await apiService.post(`/meta-api/connections/${selectedConnection.id}/templates`, {
        name: `template_${Date.now()}`, // Nome único para o novo template
        category: 'UTILITY',
        language: 'pt_BR',
        components: components
      });

      toast({
        title: "Template Criado",
        description: "Novo template criado com sucesso na API Meta."
      });

        // 2. Opcionalmente, perguntar se deseja deletar o template antigo
        if (confirm('Deseja excluir o template antigo da API Meta? Esta ação não pode ser desfeita.')) {
          try {
            // Aqui você precisaria do nome do template antigo para deletar
            // Por enquanto, vamos apenas mostrar uma mensagem
            toast({
              title: "Aviso",
              description: "Para deletar o template antigo, use o botão 'Excluir' na lista de templates.",
              variant: "destructive"
            });
          } catch (deleteError: any) {
            console.error('Erro ao deletar template antigo:', deleteError);
            toast({
              title: "Aviso",
              description: "O novo template foi criado, mas houve um erro ao remover o template antigo.",
              variant: "destructive"
            });
          }
        }

        // Recarregar templates para mostrar as alterações
        loadMetaTemplates(selectedConnection.id);
        return true;
    } catch (error) {
      console.error('Erro ao criar template na API Meta:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar template na API Meta",
        variant: "destructive"
      });
      return false;
    }
  };

  const handleCreateTemplate = async () => {
    if (!selectedConnection) return;
    
    try {
      await apiService.post('/message-templates/create', {
        whatsapp_connection_id: selectedConnection.id,
        ...formData
      });

      toast({
        title: "Sucesso",
        description: "Template criado com sucesso"
      });
      resetForm();
      loadTemplates(selectedConnection.id);
    } catch (error) {
      console.error('Erro ao criar template:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar template",
        variant: "destructive"
      });
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      await apiService.put(`/message-templates/${editingTemplate.id}`, formData);

      toast({
        title: "Sucesso",
        description: "Template atualizado com sucesso"
      });
      resetForm();
      loadTemplates(selectedConnection!.id);
    } catch (error) {
      console.error('Erro ao atualizar template:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar template",
        variant: "destructive"
      });
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (!confirm('Tem certeza que deseja deletar este template?')) return;
    
    try {
      await apiService.delete(`/message-templates/${templateId}`);

      toast({
        title: "Sucesso",
        description: "Template deletado com sucesso"
      });
      loadTemplates(selectedConnection!.id);
    } catch (error) {
      console.error('Erro ao deletar template:', error);
      toast({
        title: "Erro",
        description: "Erro ao deletar template",
        variant: "destructive"
      });
    }
  };

  const startEditing = (template: MessageTemplate) => {
    setEditingTemplate(template);
    setIsEditing(true);
    setIsCreating(false);
    setFormData({
      template_type: template.template_type,
      name: template.name,
      content: template.content,
      variables: template.variables
    });
  };

  const resetForm = () => {
    setFormData({
      template_type: '',
      name: '',
      content: '',
      variables: {}
    });
    setIsCreating(false);
    setIsEditing(false);
    setEditingTemplate(null);
    setShowPreview(null);
  };

  const copyContent = (content: string) => {
    navigator.clipboard.writeText(content);
    toast({
      title: "Copiado",
      description: "Conteúdo copiado para a área de transferência"
    });
  };

  const exportTemplatesToPDF = () => {
    if (!selectedConnection) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Título
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Templates de Mensagens', margin, yPosition);
    yPosition += 15;

    // Informações da conexão
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Conexão: ${selectedConnection.name}`, margin, yPosition);
    yPosition += 8;
    doc.text(`Tipo: ${selectedConnection.connection_type === 'oficial' ? 'API Oficial' : 'API Extra Oficial'}`, margin, yPosition);
    yPosition += 8;
    doc.text(`Data de exportação: ${new Date().toLocaleDateString('pt-BR')}`, margin, yPosition);
    yPosition += 20;

    // Templates da Meta (se conexão oficial)
    if (selectedConnection.connection_type === 'oficial' && metaTemplates.length > 0) {
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Templates da Meta API', margin, yPosition);
      yPosition += 10;

      metaTemplates.forEach((template, index) => {
        // Verificar se precisa de nova página
        if (yPosition > 250) {
          doc.addPage();
          yPosition = margin;
        }

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${template.name}`, margin, yPosition);
        yPosition += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        
        // Status e categoria
        doc.text(`Status: ${template.status} | Categoria: ${template.category}`, margin, yPosition);
        yPosition += 6;
        doc.text(`ID: ${template.id} | Idioma: ${template.language}`, margin, yPosition);
        yPosition += 8;

        // Conteúdo do template
        doc.setFontSize(10);
        const content = template.components?.find(c => c.type === 'BODY')?.text || 'Sem conteúdo';
        const splitContent = doc.splitTextToSize(content, pageWidth - 2 * margin);
        doc.text(splitContent, margin, yPosition);
        yPosition += splitContent.length * 4 + 10;
      });
    }

    // Templates customizados (se conexão extra oficial)
    if (selectedConnection.connection_type === 'extra_oficial' && templates.length > 0) {
      if (yPosition > 200) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Templates Customizados', margin, yPosition);
      yPosition += 10;

      templates.forEach((template, index) => {
        // Verificar se precisa de nova página
        if (yPosition > 250) {
          doc.addPage();
          yPosition = margin;
        }

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`${index + 1}. ${template.name}`, margin, yPosition);
        yPosition += 8;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(`Tipo: ${template.template_type} | Ativo: ${template.is_active ? 'Sim' : 'Não'}`, margin, yPosition);
        yPosition += 6;
        doc.text(`Criado em: ${new Date(template.created_at).toLocaleDateString('pt-BR')}`, margin, yPosition);
        yPosition += 8;

        // Conteúdo do template
        doc.setFontSize(10);
        const splitContent = doc.splitTextToSize(template.content, pageWidth - 2 * margin);
        doc.text(splitContent, margin, yPosition);
        yPosition += splitContent.length * 4 + 10;
      });
    }

    // Salvar o PDF
    const fileName = `templates_${selectedConnection.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    toast({
      title: "Exportação concluída",
      description: `Templates exportados para ${fileName}`
    });
  };

  const tipoSelector = (
    <Card>
      <CardContent className="py-4 flex items-center gap-3">
        <Label className="text-sm whitespace-nowrap">Tipo de mensagem:</Label>
        <Select value={tipoMensagem} onValueChange={(v) => setTipoMensagem(v as 'whatsapp' | 'email')}>
          <SelectTrigger className="w-[240px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="whatsapp">WhatsApp (Meta)</SelectItem>
            <SelectItem value="email">Email</SelectItem>
          </SelectContent>
        </Select>
      </CardContent>
    </Card>
  );

  if (tipoMensagem === 'email') {
    return (
      <div className="space-y-6">
        {tipoSelector}
        <EmailTemplatesManager clientId={clientId} />
      </div>
    );
  }

  if (isLoading) {
    return <AtomicLoadingScreen message="Carregando templates de mensagens..." />;
  }

  if (connections.length === 0) {
    return (
      <div className="space-y-6">
        {tipoSelector}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Templates de Mensagens
            </CardTitle>
            <CardDescription>
              Configure templates de mensagens para suas conexões WhatsApp
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhuma conexão WhatsApp encontrada</h3>
              <p className="text-muted-foreground mb-4">
                Você precisa configurar pelo menos uma conexão WhatsApp ativa na aba API para criar templates de mensagens.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {tipoSelector}
      {/* Seleção de Conexão - Melhorada */}
      <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50/50 to-purple-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <MessageSquare className="h-5 w-5" />
            Templates de Mensagens
          </CardTitle>
          <CardDescription className="text-blue-600">
            Configure templates de mensagens para suas conexões WhatsApp
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Status das Conexões */}
            {connections.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {connections.map((connection) => (
                  <div 
                    key={connection.id} 
                    className={`p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                      selectedConnection?.id === connection.id 
                        ? 'border-blue-500 bg-blue-100 shadow-md' 
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                    }`}
                    onClick={() => {
                      setSelectedConnection(connection);
                      resetForm();
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0">
                        {connection.connection_type === 'oficial' ? (
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Shield className="h-5 w-5 text-blue-600" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Zap className="h-5 w-5 text-green-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 truncate">
                            {connection.name}
                          </h4>
                          {selectedConnection?.id === connection.id && (
                            <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge 
                            variant={connection.connection_type === 'oficial' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {connection.connection_type === 'oficial' ? 'Meta API' : 'Extra Oficial'}
                          </Badge>
                          <Badge 
                            variant={connection.is_active ? 'default' : 'destructive'}
                            className="text-xs"
                          >
                            {connection.is_active ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-500">
                          {connection.connection_type === 'oficial' 
                            ? 'Templates aprovados pela Meta' 
                            : 'Templates personalizados'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Seleção Tradicional (Fallback) */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label htmlFor="connection-select" className="text-sm font-medium text-gray-700">
                  Conexão WhatsApp Selecionada
                </Label>
                <Select 
                  value={selectedConnection?.id.toString() || ''} 
                  onValueChange={(value) => {
                    const connection = connections.find(c => c.id === parseInt(value));
                    setSelectedConnection(connection || null);
                    resetForm();
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione uma conexão" />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map((connection) => (
                      <SelectItem key={connection.id} value={connection.id.toString()}>
                        <div className="flex items-center gap-3">
                          {connection.connection_type === 'oficial' ? (
                            <Shield className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Zap className="h-4 w-4 text-green-500" />
                          )}
                          <div>
                            <div className="font-medium">{connection.name}</div>
                            <div className="text-xs text-gray-500">
                              {connection.connection_type === 'oficial' ? 'Meta API' : 'Extra Oficial'} • 
                              {connection.is_active ? 'Ativo' : 'Inativo'}
                            </div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {selectedConnection && !selectedAtomosConfigId && (
                <div className="ml-4">
                  <Button
                    onClick={exportTemplatesToPDF}
                    variant="outline"
                    className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    <Download className="h-4 w-4" />
                    Exportar PDF
                  </Button>
                </div>
              )}
            </div>

            {/* Informações da Conexão Selecionada */}
            {selectedConnection && !selectedAtomosConfigId && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  {selectedConnection.connection_type === 'oficial' ? (
                    <Shield className="h-5 w-5 text-blue-600" />
                  ) : (
                    <Zap className="h-5 w-5 text-green-600" />
                  )}
                  <div>
                    <h4 className="font-semibold text-blue-900">
                      {selectedConnection.name}
                    </h4>
                    <p className="text-sm text-blue-700">
                      {selectedConnection.connection_type === 'oficial'
                        ? 'Conexão oficial da Meta com templates aprovados'
                        : 'Conexão extra-oficial com templates personalizados'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Seletor V2 — aparece quando o cliente tem alguma conexão V2 ativa */}
            {atomosConfigs.length > 0 && (
              <div className="border-t border-teal-200 pt-4 mt-4">
                <Label htmlFor="atomos-connection-select" className="text-sm font-medium text-teal-800">
                  Conexão V2 (ÁtomosChat)
                </Label>
                <p className="text-xs text-teal-600 mb-2">
                  Selecione uma conexão V2 para visualizar os templates aprovados nessa conta.
                </p>
                <div className="flex items-center gap-3">
                  <Select
                    value={selectedAtomosConfigId ? String(selectedAtomosConfigId) : ''}
                    onValueChange={(v) => {
                      const id = v ? Number(v) : null;
                      setSelectedAtomosConfigId(id);
                      // Ao escolher V2, limpa seleção V1 (e templates Meta) para evitar dupla exibição
                      if (id != null) {
                        setSelectedConnection(null);
                        setMetaTemplates([]);
                        setTemplates([]);
                      }
                    }}
                  >
                    <SelectTrigger className="mt-1 max-w-md">
                      <SelectValue placeholder="Selecione uma conexão V2" />
                    </SelectTrigger>
                    <SelectContent>
                      {atomosConfigs.map((cfg) => (
                        <SelectItem key={cfg.id} value={String(cfg.id)}>
                          {cfg.account_name}{cfg.channel_phone ? ` — ${cfg.channel_phone}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedAtomosConfigId && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedAtomosConfigId(null);
                        setAtomosTemplates([]);
                      }}
                    >
                      Limpar V2
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Templates V2 (ÁtomosChat) — read-only, gerenciados em chat.atomos.tech */}
      {selectedAtomosConfigId && (
        <Card className="border-2 border-teal-200 bg-teal-50/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-teal-800">
              <MessageSquare className="h-5 w-5" />
              Templates ÁtomosChat (V2) — somente leitura
            </CardTitle>
            <CardDescription className="text-teal-700">
              Templates desta conexão V2. Para criar/editar templates, use a plataforma{' '}
              <a href="https://chat.atomos.tech/" target="_blank" rel="noopener noreferrer" className="underline">
                chat.atomos.tech
              </a>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingAtomosTemplates ? (
              <div className="text-sm text-gray-600">Carregando templates...</div>
            ) : atomosTemplates.length === 0 ? (
              <div className="text-sm text-gray-600">Nenhum template encontrado para esta conexão.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {atomosTemplates.map((tpl) => (
                  <div key={tpl.id} className="border rounded-lg p-3 bg-white">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="font-medium text-sm text-gray-900 truncate">{tpl.name}</div>
                      <Badge variant="outline" className="text-xs">{tpl.status}</Badge>
                    </div>
                    <div className="text-xs text-gray-600 mb-2">
                      {tpl.categoryName ?? '-'}{tpl.fileType ? ` · ${tpl.fileType}` : ''}
                    </div>
                    {tpl.text && (
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans bg-gray-50 rounded p-2 max-h-32 overflow-auto">
                        {tpl.text}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {selectedConnection && (
        <>
          {/* Botão Criar Novo Template */}
          {!isCreating && !isEditing && selectedConnection.connection_type === 'extra_oficial' && (
            <div className="flex justify-end">
              <Button onClick={() => setIsCreating(true)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Novo Template
              </Button>
            </div>
          )}

          {/* Formulário de Criação/Edição */}
          {(isCreating || isEditing) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {isEditing ? <Edit2 className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
                  {isEditing ? 'Editar Template' : 'Novo Template'}
                </CardTitle>
                <CardDescription>
                  Configure o template de mensagem para {selectedConnection.connection_type === 'oficial' ? 'API Oficial' : 'API Extra Oficial'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="template-type">Tipo de Template</Label>
                    <Select 
                      value={formData.template_type} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, template_type: value }))}
                      disabled={isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {templateTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="template-name">Nome do Template</Label>
                    <Input
                      id="template-name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Lembrete de Vencimento - Taxa Administrativa"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="template-content">Conteúdo do Template</Label>
                  <Textarea
                    id="template-content"
                    value={formData.content}
                    onChange={(e) => handleContentChange(e.target.value)}
                    placeholder={selectedConnection.connection_type === 'oficial' 
                      ? "Use {{1}}, {{2}}, {{3}} para variáveis..."
                      : "Use {nome}, {placa}, {valor} para variáveis..."
                    }
                    rows={8}
                    className="font-mono"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedConnection.connection_type === 'oficial' 
                      ? "API Oficial: Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis dinâmicas"
                      : "API Extra Oficial: Use {'{nome}'}, {'{placa}'}, {'{valor}'}, etc. para variáveis"
                    }
                  </p>
                </div>

                {/* Campos de Variáveis */}
                {Object.keys(formData.variables).length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <Label>Valores das Variáveis (Preview)</Label>
                      <Badge variant="outline">
                        {Object.keys(formData.variables).length} variáveis detectadas
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.keys(formData.variables).map((variable) => (
                        <div key={variable}>
                          <Label htmlFor={`var-${variable}`} className="text-sm font-mono">
                            {variable}
                          </Label>
                          <Input
                            id={`var-${variable}`}
                            value={formData.variables[variable]}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              variables: { ...prev.variables, [variable]: e.target.value }
                            }))}
                            placeholder="Valor para preview"
                            className="font-mono text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Preview */}
                {formData.content && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label>Preview da Mensagem</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyContent(generatePreview(formData.content, formData.variables))}
                        className="h-7"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="p-3 bg-muted rounded-md">
                      <pre className="whitespace-pre-wrap text-sm">{generatePreview(formData.content, formData.variables)}</pre>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={resetForm}>
                    <X className="h-4 w-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button onClick={isEditing ? handleUpdateTemplate : handleCreateTemplate}>
                    <Save className="h-4 w-4 mr-2" />
                    {isEditing ? 'Atualizar' : 'Salvar'} Template
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Lista de Templates */}
          {!isCreating && !isEditing && (
            <div className="space-y-4">
              {selectedConnection.connection_type === 'oficial' ? (
                // Templates da Meta API
                metaTemplates.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-8">
                      <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium mb-2">Nenhum template encontrado</h3>
                      <p className="text-muted-foreground mb-4">
                        Nenhum template foi encontrado na API Meta para esta conexão.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {metaTemplates.map((template) => (
                      <Card key={template.id} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-base font-semibold">{template.name}</CardTitle>
                              <div className="flex gap-2 mt-2">
                                <Badge variant="outline" className="text-xs">
                                  {template.category}
                                </Badge>
                                <Badge variant="default" className="text-xs">
                                  {template.status === 'APPROVED' ? 'Aprovado' : template.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="space-y-3">
                            <div>
                              <Label className="text-sm font-medium">Conteúdo:</Label>
                              <div className="p-2 border rounded text-sm mt-1 max-h-20 overflow-y-auto">
                                <pre className="whitespace-pre-wrap text-xs">{getTemplateContent(template)}</pre>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => openViewModal(template)}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Visualizar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => openEditModal(template)}
                              >
                                <Edit2 className="h-4 w-4 mr-1" />
                                Editar
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )
              ) : (
                // Templates Extra Oficiais
                templates.length === 0 ? (
                  <Card>
                    <CardContent className="text-center py-8">
                      <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium mb-2">Nenhum template encontrado</h3>
                      <p className="text-muted-foreground mb-4">
                        Crie seu primeiro template de mensagem para esta conexão WhatsApp.
                      </p>
                      <Button onClick={() => setIsCreating(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Criar Template
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  templates.map((template) => (
                    <Card key={template.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div>
                              <CardTitle className="text-lg">{template.name}</CardTitle>
                              <CardDescription>
                                <Badge variant="outline" className="mr-2">
                                  {templateTypes.find(t => t.value === template.template_type)?.label}
                                </Badge>
                                {template.is_active ? (
                                  <Badge variant="default">Ativo</Badge>
                                ) : (
                                  <Badge variant="secondary">Inativo</Badge>
                                )}
                              </CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowPreview(showPreview === template.id.toString() ? null : template.id.toString())}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openVariableMappingModal(template)}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => startEditing(template)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeleteTemplate(template.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      
                      {showPreview === template.id.toString() && (
                        <CardContent>
                          <Separator className="mb-4" />
                          <div className="space-y-4">
                            <div>
                              <Label className="text-sm font-medium">Conteúdo Original:</Label>
                              <div className="p-3 bg-muted rounded-md mt-1">
                                <pre className="whitespace-pre-wrap text-sm font-mono">{template.content}</pre>
                              </div>
                            </div>
                            
                            {Object.keys(template.variables).length > 0 && (
                              <div>
                                <Label className="text-sm font-medium">
                                  Variáveis ({Object.keys(template.variables).length}):
                                </Label>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                                  {Object.entries(template.variables).map(([key, value]) => (
                                    <div key={key} className="text-xs">
                                      <span className="font-mono font-medium">{key}:</span>
                                      <span className="ml-1 text-muted-foreground">
                                        {value || '[não definido]'}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <Label className="text-sm font-medium">Preview:</Label>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    let preview = template.content;
                                    Object.entries(template.variables).forEach(([key, value]) => {
                                      if (value) {
                                        const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                                        preview = preview.replace(regex, value);
                                      }
                                    });
                                    copyContent(preview);
                                  }}
                                  className="h-6"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </div>
                              <div className="p-3 bg-background border rounded-md">
                                <pre className="whitespace-pre-wrap text-sm">
                                  {(() => {
                                    let preview = template.content;
                                    Object.entries(template.variables).forEach(([key, value]) => {
                                      if (value) {
                                        const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                                        preview = preview.replace(regex, value);
                                      }
                                    });
                                    return preview;
                                  })()}
                                </pre>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))
                )
              )}
            </div>
          )}
        </>
      )}

      {/* Modal de Edição de Template da Meta */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5" />
              Editar Template: {editingMetaTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Edite o conteúdo do template e visualize o preview com as variáveis preenchidas.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label htmlFor="edit-content">Conteúdo do Template</Label>
              <Textarea
                id="edit-content"
                value={editFormData.content}
                onChange={(e) => handleEditContentChange(e.target.value)}
                placeholder={'Use {{1}}, {{2}}, {{3}} para variáveis...'}
                rows={8}
                className="font-mono"
              />
              <p className="text-sm text-muted-foreground mt-1">
                API Oficial Meta: Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis dinâmicas
              </p>
            </div>

            {/* Campos de Variáveis */}
            {Object.keys(editFormData.variables).length > 0 && (
              <div className="space-y-6 p-4 border rounded-lg bg-muted/30">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-semibold">Valores das Variáveis (Preview)</Label>
                  <Badge variant="outline">
                    {Object.keys(editFormData.variables).length} variáveis detectadas
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.keys(editFormData.variables).map((variable) => (
                    <div key={variable} className="space-y-2">
                      <Label htmlFor={`edit-var-${variable}`} className="text-sm font-mono font-medium">
                        {variable}
                      </Label>
                      <Input
                        id={`edit-var-${variable}`}
                        value={editFormData.variables[variable]}
                        onChange={(e) => setEditFormData(prev => ({
                          ...prev,
                          variables: { ...prev.variables, [variable]: e.target.value }
                        }))}
                        placeholder="Valor para preview"
                        className="font-mono text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preview */}
            {editFormData.content && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Preview da Mensagem</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyContent(generatePreview(editFormData.content, editFormData.variables))}
                    className="h-7"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <div className="p-4 border rounded-md">
                  <pre className="whitespace-pre-wrap text-sm">{generatePreview(editFormData.content, editFormData.variables)}</pre>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  copyContent(generatePreview(editFormData.content, editFormData.variables));
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar Preview
              </Button>
              <Button 
                onClick={async () => {
                  if (editingMetaTemplate) {
                    const success = await saveTemplateChanges(editingMetaTemplate.id, editFormData.content);
                    if (success) {
                      setShowEditModal(false);
                    }
                  }
                }}
              >
                <Save className="h-4 w-4 mr-2" />
                Salvar Alterações
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Visualização de Template da Meta */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Visualizar Template: {viewingTemplate?.name}
            </DialogTitle>
            <DialogDescription>
              Visualize o conteúdo do template e configure as variáveis para preview.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Preview */}
            {viewFormData.content && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Preview da Mensagem</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyContent(generatePreview(viewFormData.content, viewFormData.variables))}
                    className="h-7"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <div className="p-4 border rounded-md">
                  <pre className="whitespace-pre-wrap text-sm">{generatePreview(viewFormData.content, viewFormData.variables)}</pre>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowViewModal(false)}>
                <X className="h-4 w-4 mr-2" />
                Fechar
              </Button>
              <Button 
                onClick={() => {
                  copyContent(generatePreview(viewFormData.content, viewFormData.variables));
                }}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copiar Preview
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Mapeamento de Variáveis */}
      <Dialog open={showVariableMappingModal} onOpenChange={setShowVariableMappingModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="modal-variaveis-v2">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Mapeamento de Variáveis: {editingTemplateForMapping?.name}
            </DialogTitle>
            <DialogDescription>
              Configure o mapeamento das variáveis {`{{1}}`} a {`{{6}}`} para os campos do banco de dados.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <Label className="text-base font-semibold">Conteúdo do Template</Label>
              <div className="p-3 bg-muted rounded-md mt-2">
                <pre className="whitespace-pre-wrap text-sm font-mono">{editingTemplateForMapping?.content}</pre>
              </div>
            </div>

            <div className="space-y-6 p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <Label className="text-base font-semibold">Mapeamento de Variáveis</Label>
                <Badge variant="outline">6 placeholders fixos</Badge>
              </div>
              
              <div className="space-y-4">
                {['{{1}}', '{{2}}', '{{3}}', '{{4}}', '{{5}}', '{{6}}'].map((placeholder) => (
                  <div key={placeholder} className="flex items-center gap-4">
                    <div className="w-16 text-sm font-mono font-medium text-center">
                      {placeholder}
                    </div>
                    <div className="flex-1">
                      <Select
                        value={variableMappings[placeholder]}
                        onValueChange={(value) => {
                          console.log(`🔍 [MessagesTab] Dropdown ${placeholder} mudou para:`, value);
                          setVariableMappings(prev => ({
                            ...prev,
                            [placeholder]: value
                          }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o campo" />
                        </SelectTrigger>
                        <SelectContent>
                          {variableMappingOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Preview com Mapeamento */}
            {editingTemplateForMapping && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label>Preview da Mensagem</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyContent(generateMappedPreview(editingTemplateForMapping.content, variableMappings))}
                    className="h-7"
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <div className="p-4 border rounded-md">
                  <pre className="whitespace-pre-wrap text-sm">
                    {generateMappedPreview(editingTemplateForMapping.content, variableMappings)}
                  </pre>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowVariableMappingModal(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancelar
              </Button>
              <Button onClick={saveVariableMapping}>
                <Save className="h-4 w-4 mr-2" />
                Salvar Mapeamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
